import { useState, useEffect } from "react";
import {
  Form,
  Button,
  Spinner,
  Row,
  Col,
  Card,
  InputGroup,
  Alert,
} from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useInspectionUserMutation } from "../../../../../api/inspection/inspectionUsers.hooks";

const InspectionUserForm = ({
  onCancel,
  onSave,
  user = null,
  mode = "create", // 'create', 'edit', 'view'
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    deviceToken: "",
    isFirstLogin: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const { createUser, updateUser, loading, error } = useInspectionUserMutation();

  // Get token from session storage
  const storedUser = sessionStorage.getItem("userDetails");
  let token = null;
  if (storedUser) {
    const userDetails = JSON.parse(storedUser);
    token = userDetails?.token;
  }

  // ✅ Role options (no hierarchy)
  const roleOptions = [
    { value: "Super Admin", label: "Super Admin" },
    { value: "Admin", label: "Admin" },
    { value: "Electrical Engineer", label: "Electrical Engineer" },
    { value: "Mechanical Engineer", label: "Mechanical Engineer" },
    { value: "Weather Forecaster", label: "Weather Forecaster" },
    { value: "Electrical Staff", label: "Electrical Staff" },
    { value: "Mechanical Staff", label: "Mechanical Staff" },
    { value: "Store Keeper", label: "Store Keeper" },
  ];

  // ✅ Prefill for Edit/View
  useEffect(() => {
    if (user && (mode === "edit" || mode === "view")) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role?.trim() || "",
        deviceToken: user.deviceToken || "",
        isFirstLogin:
          user.isFirstLogin !== undefined ? user.isFirstLogin : true,
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "",
        deviceToken: "",
        isFirstLogin: true,
      });
    }
    setValidationErrors({});
  }, [user, mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (mode === "edit" && formData.password.trim() && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.role || !formData.role.trim()) {
      errors.role = "Role is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === "view") {
      onCancel();
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role.trim(),
      };

      if (mode === "edit") {
        if (formData.password.trim()) {
          payload.password = formData.password;
        }
        if (formData.deviceToken) {
          payload.deviceToken = formData.deviceToken.trim();
        }
        if (formData.isFirstLogin !== undefined) {
          payload.isFirstLogin = formData.isFirstLogin;
        }
      }

      if (mode === "create") {
        await createUser(payload, token);
        onSave && onSave(payload);
      } else if (mode === "edit" && user?._id) {
        await updateUser(user._id, payload, token);
        onSave && onSave(payload);
      }
    } catch (err) {
      console.error("Inspection user mutation error:", err);
    }
  };

  const getFormTitle = () => {
    switch (mode) {
      case "create":
        return "Add New Inspection User";
      case "edit":
        return "Edit Inspection User";
      case "view":
        return "View Inspection User Details";
      default:
        return "Inspection User";
    }
  };

  const isReadOnly = mode === "view";

  return (
    <Card className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={onCancel}>
          ← Back
        </Button>
        <h4 className="m-0">{getFormTitle()}</h4>
      </div>

      {error && (
        <Alert variant="danger" dismissible>
          {error}
        </Alert>
      )}

      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Username {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                isInvalid={!!validationErrors.name}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Email {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                isInvalid={!!validationErrors.email}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Role {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Select
                value={formData.role || ""}
                onChange={(e) => handleInputChange("role", e.target.value)}
                isInvalid={!!validationErrors.role}
                disabled={isReadOnly}
              >
                <option value="">Select Role</option>
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.role}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {mode === "edit" && (
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Password{" "}
                  <small className="text-muted">
                    (Leave blank to keep current)
                  </small>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={formData.password || ""}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    isInvalid={!!validationErrors.password}
                    disabled={isReadOnly}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isReadOnly}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.password}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          )}
        </Row>

        {mode === "edit" && (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Device Token</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter device token (optional)"
                  value={formData.deviceToken}
                  onChange={(e) =>
                    handleInputChange("deviceToken", e.target.value)
                  }
                  disabled={isReadOnly}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Is First Login</Form.Label>
                <Form.Select
                  value={formData.isFirstLogin.toString()}
                  onChange={(e) =>
                    handleInputChange("isFirstLogin", e.target.value === "true")
                  }
                  disabled={isReadOnly}
                >
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}

        {mode === "view" && user && (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Created Date</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "-"
                    }
                    disabled
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    type="text"
                    value={user.isActive ? "Active" : "Inactive"}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>

            {user.deviceToken && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Device Token</Form.Label>
                    <Form.Control type="text" value={user.deviceToken} disabled />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Is First Login</Form.Label>
                    <Form.Control
                      type="text"
                      value={user.isFirstLogin ? "Yes" : "No"}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </>
        )}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode !== "view" && (
            <Button variant="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {mode === "create" ? "Adding..." : "Saving..."}
                </>
              ) : mode === "create" ? (
                "Add User"
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default InspectionUserForm;
